import React, { useEffect, useMemo, useState } from 'react';
import { useCurrentProject } from '@/shared/hooks/useCurrentProject';
import { CalendarCheck, Plus } from 'lucide-react';
import { BaseLayout } from '@/shared/components/layout';
import { Button } from '@/shared/ui/button';
import type { MeetingFilters } from '../types';
import { useMeetings } from '../hooks';
import { MeetingFormDialog } from './MeetingFormDialog';
import { MeetingsTable } from './MeetingsTable';
import { MEETINGS_LIST_TOUR_ID } from '@/features/tours/meetings/list';
import { MEETINGS_COMBINED_TOUR_ID } from '@/features/tours/meetings/combined';
import '@/features/tours/meetings/detail';
import { useTour } from '@/features/tours';

export const MeetingsPage: React.FC = () => {
  const { projectIdNumber: numericProjectId } = useCurrentProject();

  const [filters, setFilters] = useState<MeetingFilters>({
    page: 1,
    page_size: 20,
  });
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const headerSubtitle = useMemo(() => {
    if (!numericProjectId) {
      return 'Select a project to view meetings.';
    }
    return 'Agendas, attendees, and decisions.';
  }, [numericProjectId]);

  const { data: meetingsData } = useMeetings(numericProjectId, filters);
  const { hydrated: tourHydrated, active, startTour, hasSeen } = useTour();
  const hasItems = (meetingsData?.results ?? []).length >= 1;
  useEffect(() => {
    if (!tourHydrated) return;
    if (active) return;
    if (!numericProjectId) return;
    if (hasItems) {
      if (hasSeen(MEETINGS_COMBINED_TOUR_ID)) return;
      if (hasSeen(MEETINGS_LIST_TOUR_ID)) return;
      void startTour(MEETINGS_COMBINED_TOUR_ID);
    } else {
      if (hasSeen(MEETINGS_LIST_TOUR_ID)) return;
      void startTour(MEETINGS_LIST_TOUR_ID);
    }
  }, [tourHydrated, active, startTour, hasSeen, hasItems, numericProjectId]);

  return (
    <BaseLayout>
      <div className="mx-auto w-full min-w-0 max-w-7xl space-y-6 p-4 sm:p-6">
        <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 items-start gap-2">
            <CalendarCheck className="h-6 w-6 text-primary" />
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold">Meeting Minutes</h1>
              <p className="text-sm text-muted-foreground">{headerSubtitle}</p>
            </div>
          </div>
          <Button
            onClick={() => setIsCreateOpen(true)}
            disabled={!numericProjectId}
            data-tour-id="meetings-new"
            className="w-full sm:w-auto"
          >
            <Plus className="mr-2 h-4 w-4" />
            New Meeting
          </Button>
        </div>

        <MeetingsTable
          projectId={numericProjectId}
          filters={filters}
          onFiltersChange={setFilters}
        />
      </div>

      <MeetingFormDialog
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        projectId={numericProjectId}
      />
    </BaseLayout>
  );
};
